<template>
  <div class="flex items-center overflow-overlay overflow-y-hidden">
    <template v-if="supportMultiColors">
      <template
        v-for="(color, index) in editorStore.theme?.colorGroupTable ?? []"
        :key="color.id"
      >
        <drop-down
          :menus="getRightMenus(color as StoreThemeColorItem)"
          trigger="contextmenu"
        >
          <div
            class="h-10 text-[12px] text-white font-semibold px-2.5 min-w-[64px] rounded-lg flex items-center"
            :class="{
              'bg-[#515151]': color.id == editorStore.themeColorId,
              ' !text-white/30': isAOD && layerStore.isSingleAOD
            }"
            data-tab-node="true"
            tabindex="-1"
            @click="() => exchangeColor(color.id, index)"
          >
            <FlexibleInput
              :ref="(el) => el && (editableRefs[color.id] = el as unknown as typeof FlexibleInput)"
              v-model="color.name"
              class="focus:outline-none"
              :validator="(val:string) => val.length ? { valid: true } : { valid:false, msg:'配色名称不能为空' }"
              quiet
              @update="(val: string) => editorStore.theme && updateThemeColorAttr(editorStore.theme, color.id, { name: val })"
            />
            <div
              class="border-[1px] border-[black]/[0.6] w-3 h-3 z-[2] ml-1 rounded-lg"
              :style="{
                'background-color': color.color,
                opacity: isAOD ? 0.3 : 1
              }"
            ></div>
          </div>
        </drop-down>
      </template>

      <a-tooltip title="新建配色" placement="bottom">
        <div
          v-if="!isAOD"
          class="text-xl font-light w-12 h-10 text-center bg-[#313131] px-1 flex justify-center items-center sticky right-0 z-10"
          @click="add"
        >
          <icon :icon="'add.svg'" :width="12" />
        </div>
      </a-tooltip>
      <a-modal
        v-model:visible="visible"
        title="选择配色"
        ok-text="确定"
        cancel-text="取消"
        class="flex items-center"
      >
        <div tabindex="-1" @dblclick="onDbClick" @keyup.enter="onDbClick">
          <color-picker
            is-widget
            round-history
            picker-type="chrome"
            disable-alpha
            disable-history
            format="hex6"
            @update:pure-color="onColorChange"
          />
          <div>
            <div class="mt-4 text-center text-base">
              <span>已选色值：{{ pureColor ?? '-' }}</span>
              <div v-if="exists" class="text-red-500">
                该色值已存在，请选择其他色值!
              </div>
            </div>
          </div>
        </div>

        <template #footer>
          <a-button @click="() => (visible = false)">取消</a-button>
          <a-button
            type="primary"
            :disabled="!pureColor || !!exists"
            @click="onConfirmColor"
          >
            确定
          </a-button>
        </template>
      </a-modal>
    </template>
    <template v-else>
      <div
        class="h-10 text-[12px] text-white font-semibold px-2.5 min-w-[64px] rounded-lg flex justify-center items-center bg-[#515151]"
      >
        默认配色
      </div>
    </template>

    <!-- <div>{{ history }}</div> -->
    <modal-theme-color-oversize ref="overSizeModal" />
  </div>
</template>

<script setup lang="ts">
import { useGlobalShortcuts, useThemeManage } from '@/hooks';
// import { useSnapshot } from '@/hooks/use-snapshot';
import { useEditorStore } from '@/store/use-editor-store';
import { useLayerStore } from '@/store/use-layer-store';
import { message, notification } from 'ant-design-vue';
import { computed, nextTick, ref } from 'vue';
import FlexibleInput from '../common/FlexibleInput.vue';
import { GlobalConfig } from '@/services/gloabl.config';
import { canUse } from '@/utils/can-use.utils';
// import { useSnapshot } from '@/hooks/use-snapshot';

// const { history } = useSnapshot();
// const isDev = import.meta.env.DEV;
const editorStore = useEditorStore();
const layerStore = useLayerStore();
const { addThemeColor, deleteThemeColor, updateThemeColorAttr } =
  useThemeManage();

const isAOD = computed(() => editorStore.theme?.type == 'AOD');

const editableRefs = ref<{ [key: string]: typeof FlexibleInput }>({});

// 切换配色
const exchangeColor = (colorId: string, index: number) => {
  if (colorId === editorStore.themeColor?.id) return;
  if (isAOD.value && layerStore.isSingleAOD) {
    if (index > 0) {
      message.destroy();
      message.info({
        content: '配置其他配色下的息屏，请前往「多配色息屏」',
        style: {
          marginTop: '80vh'
        }
      });
    }
  } else {
    editorStore.selectThemeColor(colorId);
  }
};

const supportMultiColors = computed(() => {
  // 判断设备是否支持
  const support = canUse('multi-color-group').valid;
  if (!support) return false;

  // 息屏模式下，仅允许多配色息屏模式下，选择多配色， 如果是单一息屏，则表示不允许设置多配色
  if (isAOD.value && editorStore.theme?.isSingleAOD) {
    return false;
  }
  return true;
});

const getRightMenus = (item: StoreThemeColorItem) => {
  const res: DropDownMenuItem[] = [
    {
      title: '复制',
      shortcuts: ['CommandOrControl', 'C'],
      onClick() {
        openPickerModal(item);
      }
    },
    {
      title: '重命名',
      shortcuts: ['CommandOrControl', 'R'],
      onClick() {
        editableRefs.value[item.id]?.startEdit?.();
      }
    },
    {
      title: '删除',
      disabled: editorStore.theme?.colorGroupTable?.[0].id == item.id,
      onClick() {
        // 仅能在亮屏下删除配色
        if (editorStore.theme?.type == 'normal') {
          deleteThemeColor(editorStore.theme, item.id);
          editorStore.selectThemeColor(editorStore.theme.colorGroupTable[0].id);
        }
      }
    }
  ];
  if (isAOD.value) {
    // 息屏模式下仅允许重命名
    return [res[1]];
  } else {
    return res;
  }
};

// picker modal
const visible = ref<boolean>(false);
const pureColor = ref<string>();
const copyFrom = ref<StoreThemeColorItem>();
const openPickerModal = async (_copyFrom?: StoreThemeColorItem) => {
  pickerDest.value = 'new';
  copyFrom.value = _copyFrom;
  visible.value = true;
  await nextTick();
  pureColor.value = '';
};

const onColorChange = (color: string) => {
  pureColor.value = color;
};

const _exists = (color: string) => {
  return !!editorStore.theme?.colorGroupTable.some(
    (item) => item.color == color
  );
};

const exists = computed(() => {
  return pureColor.value && _exists(pureColor.value);
});

const onConfirmColor = async () => {
  const color = pureColor.value as string;
  if (pickerDest.value === 'new') {
    const theme = editorStore.theme;
    if (!theme || !color || _exists(color)) return;
    pureColor.value = '';
    addThemeColor(theme, color, copyFrom.value?.id);
    notification.success({
      message: '添加成功',
      description: ''
    });
  } else if (toEdit.value) {
    updateThemeColorAttr(editorStore.theme as StoreThemeItem, toEdit.value.id, {
      color
    });
  }
  visible.value = false;
};

useGlobalShortcuts(
  [
    {
      keyboardKeys: ['CommandOrControl', 'R'],
      handler: () => {
        if (editorStore.themeColor)
          editableRefs.value[editorStore.themeColor.id]?.startEdit?.();
      }
    },
    {
      keyboardKeys: ['CommandOrControl', 'C'],
      handler: () => {
        if (!isAOD.value && editorStore.themeColor)
          openPickerModal(editorStore.themeColor);
      }
    }
  ],
  (e: KeyboardEvent) => {
    const targert = e.target as HTMLDivElement;
    return !!targert.dataset?.tabNode;
  }
);

const pickerDest = ref<'new' | 'edit'>('new');
const toEdit = ref<StoreThemeColorItem | null>(null);

const overSizeModal = ref();
const add = () => {
  const theme = editorStore.theme;
  if (!theme) return;
  // 获取当前有多少个不同的颜色
  const maxCount = GlobalConfig.themeColorMax;
  const colorsWhole: string[] = [];
  layerStore.themes.forEach((item) => {
    item.colorGroupTable.forEach((item2) => {
      const color = item2.color;
      if (!colorsWhole.includes(color)) colorsWhole.push(color);
    });
  });
  if (
    layerStore.themes.some(
      (theme) => theme.colorGroupTable.length >= maxCount
    ) ||
    colorsWhole.length >= maxCount
  ) {
    overSizeModal.value?.open?.();
  } else {
    openPickerModal(theme.colorGroupTable?.[0]);
  }
};

const onDbClick = () => {
  onConfirmColor();
};
</script>

<style lang="less"></style>
